import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { swatchesApi } from '../services/api';
import { useAuth } from './AuthContext';
import { useTodos } from './TodoContext';
import type { Swatch, SwatchCreateInput, SwatchUpdateInput } from '../types';

interface SwatchContextType {
  swatches: Swatch[];
  loading: boolean;
  fetchSwatches: () => Promise<void>;
  createSwatch: (data: SwatchCreateInput) => Promise<Swatch>;
  updateSwatch: (id: string, data: SwatchUpdateInput) => Promise<Swatch>;
  deleteSwatch: (id: string) => Promise<void>;
  handleSSEEvent: (type: string, data: unknown) => void;
}

const SwatchContext = createContext<SwatchContextType | null>(null);

export function SwatchProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { fetchCategories, fetchLists } = useTodos();
  const [swatches, setSwatches] = useState<Swatch[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSwatches = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const response = await swatchesApi.list();
      if (response.success && response.data) {
        setSwatches(response.data.swatches);
      }
    } catch (err) {
      console.error('Failed to fetch swatches:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchSwatches();
    } else {
      setSwatches([]);
    }
  }, [user, fetchSwatches]);

  const createSwatch = async (data: SwatchCreateInput): Promise<Swatch> => {
    const response = await swatchesApi.create(data);
    if (response.success && response.data) {
      const newSwatch = response.data.swatch;
      setSwatches((prev) => [...prev, newSwatch].sort((a, b) => a.position - b.position));
      return newSwatch;
    }
    throw new Error(response.error || 'Failed to create swatch');
  };

  const updateSwatch = async (id: string, data: SwatchUpdateInput): Promise<Swatch> => {
    const response = await swatchesApi.update(id, data);
    if (response.success && response.data) {
      const updatedSwatch = response.data.swatch;
      setSwatches((prev) =>
        prev
          .map((swatch) => (swatch._id === id ? updatedSwatch : swatch))
          .sort((a, b) => a.position - b.position)
      );
      // Refetch categories and lists to update linked colors
      if (data.color) {
        fetchCategories();
        fetchLists();
      }
      return updatedSwatch;
    }
    throw new Error(response.error || 'Failed to update swatch');
  };

  const deleteSwatch = async (id: string): Promise<void> => {
    const response = await swatchesApi.delete(id);
    if (response.success) {
      setSwatches((prev) => prev.filter((swatch) => swatch._id !== id));
    } else {
      throw new Error(response.error || 'Failed to delete swatch');
    }
  };

  const handleSSEEvent = useCallback((type: string, data: unknown) => {
    switch (type) {
      case 'swatch:created':
        setSwatches((prev) => {
          const swatch = data as Swatch;
          if (prev.find((s) => s._id === swatch._id)) return prev;
          return [...prev, swatch].sort((a, b) => a.position - b.position);
        });
        break;
      case 'swatch:updated':
        setSwatches((prev) =>
          prev
            .map((swatch) => (swatch._id === (data as Swatch)._id ? (data as Swatch) : swatch))
            .sort((a, b) => a.position - b.position)
        );
        // Refetch categories and lists to update linked colors
        fetchCategories();
        fetchLists();
        break;
      case 'swatch:deleted':
        setSwatches((prev) => prev.filter((swatch) => swatch._id !== (data as { id: string }).id));
        break;
      case 'swatches:reordered': {
        const { swatches: reorderedSwatches } = data as { swatches: Array<{ id: string; position: number }> };
        setSwatches((prev) => {
          const updated = [...prev];
          reorderedSwatches.forEach(({ id, position }) => {
            const idx = updated.findIndex((s) => s._id === id);
            if (idx !== -1) {
              updated[idx] = { ...updated[idx], position };
            }
          });
          return updated.sort((a, b) => a.position - b.position);
        });
        break;
      }
    }
  }, [fetchCategories, fetchLists]);

  return (
    <SwatchContext.Provider
      value={{
        swatches,
        loading,
        fetchSwatches,
        createSwatch,
        updateSwatch,
        deleteSwatch,
        handleSSEEvent,
      }}
    >
      {children}
    </SwatchContext.Provider>
  );
}

export function useSwatches() {
  const context = useContext(SwatchContext);
  if (!context) {
    throw new Error('useSwatches must be used within a SwatchProvider');
  }
  return context;
}
